const ACCESS_TOKEN_KEY = 'skymap_access_token';

/**
 * A failed API call.
 *
 * `status` is the HTTP status when the API answered directly. It is undefined
 * when CloudFront swallowed the real status — see the note in apiFetch — so
 * prefer `notFound` over comparing `status` to 404.
 */
export class ApiError extends Error {
  readonly status?: number;
  readonly notFound: boolean;

  constructor(message: string, status?: number, notFound = false) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.notFound = notFound;
  }
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(path, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(
      (err as { error?: string }).error ?? res.statusText,
      res.status,
      res.status === 404,
    );
  }

  // CloudFront's SPA fallback rewrites 403 and 404 from ANY origin — the API
  // included — into /index.html with status 200 (docs-construct errorResponses).
  // So a JSON endpoint answering with HTML has not succeeded: the API said 403
  // or 404 and the status was lost on the way back. Without this guard the
  // res.json() below throws a bare "Unexpected token '<'" at the caller.
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    throw new ApiError(`Not found (${path})`, undefined, true);
  }

  return res.json() as Promise<T>;
}
